import React from "react";
import { Link } from "react-router-dom";
import { Euro, Home, FileText, Bus, Briefcase, ArrowRight, CheckCircle2, Globe2 } from "lucide-react";
import WorkLayout from "@/components/WorkLayout";

const NL_HERO = "https://images.unsplash.com/photo-1584003564911-a7a321c84e1c?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80";
const NL_2 = "https://images.unsplash.com/photo-1587291376001-b838207de820?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80";

const SALARIES = [
  ["Warehouse operative / order picker", "€2.100 – €2.600", "€28k – €34k"],
  ["Forklift / reach-truck driver", "€2.300 – €2.900", "€30k – €37k"],
  ["Production / machine operator", "€2.300 – €3.000", "€29k – €37k"],
  ["Delivery driver (B licence)", "€2.200 – €2.700", "€28k – €33k"],
  ["Truck driver (CE + Code 95)", "€2.800 – €3.600", "€36k – €45k"],
  ["Cleaning / housekeeping", "€1.900 – €2.300", "€24k – €28k"],
];

const Card = ({ icon: Icon, title, children }) => (
  <div className="rounded-2xl border border-white/10 bg-slate-900/40 p-6">
    <div className="w-11 h-11 rounded-xl bg-teal-500/10 border border-teal-500/25 flex items-center justify-center text-teal-300 mb-3"><Icon size={20} /></div>
    <h3 className="font-display font-semibold text-slate-100 mb-1.5">{title}</h3>
    <p className="text-sm text-slate-400 leading-relaxed">{children}</p>
  </div>
);

export default function LiveInNetherlands() {
  const jsonLd = {
    "@context": "https://schema.org", "@type": "Article",
    headline: "Live and Work in the Netherlands — Salaries, Housing & How to Start",
    about: "Working in the Netherlands as an international candidate",
  };
  return (
    <WorkLayout
      title="Live & Work in the Netherlands — Salaries, Housing & How to Start (2026) | WorkOnIT"
      description="A practical guide to living and working in the Netherlands: typical salaries for warehouse, production and driving jobs, housing, the 30% ruling, BSN, health insurance and how to start. Apply on WorkOnIT."
      canonicalPath="/work/live-in-netherlands" image={NL_HERO} jsonLd={jsonLd}>

      <section className="relative">
        <div className="absolute inset-0"><img src={NL_HERO} alt="Amsterdam canal" className="w-full h-full object-cover" /><div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/85 to-slate-950/50" /></div>
        <div className="relative max-w-5xl mx-auto px-5 py-24 sm:py-32">
          <span className="inline-flex items-center gap-2 text-xs font-mono tracking-wider uppercase text-teal-300 bg-teal-500/10 border border-teal-500/25 rounded-full px-3 py-1"><Globe2 size={13} /> Relocation guide</span>
          <h1 className="mt-4 font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-tight max-w-3xl">Live &amp; work in the Netherlands</h1>
          <p className="mt-4 text-lg text-slate-300 max-w-2xl">Great pay, strong worker protection and thousands of open jobs in warehouse, production and transport — the Netherlands is one of the easiest countries in Europe to start earning quickly.</p>
          <Link to="/register" data-testid="nl-guide-cta" className="mt-7 inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-teal-400 to-cyan-500 text-slate-900 font-semibold">Start your NL job search <ArrowRight size={17} /></Link>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-5 py-14">
        <h2 className="font-display font-bold text-2xl mb-2 flex items-center gap-2"><Euro size={20} className="text-teal-400" /> Typical salaries (2026)</h2>
        <p className="text-slate-400 text-sm mb-6">Indicative gross pay for common roles. Many jobs add shift bonuses, holiday allowance (8%) and overtime.</p>
        <div className="overflow-hidden rounded-2xl border border-white/10">
          <table className="w-full text-sm">
            <thead className="bg-slate-900/70 text-slate-300"><tr>
              <th className="text-left font-semibold px-4 py-3">Role</th>
              <th className="text-left font-semibold px-4 py-3">Gross / month</th>
              <th className="text-left font-semibold px-4 py-3 hidden sm:table-cell">Approx / year</th>
            </tr></thead>
            <tbody>
              {SALARIES.map((r, i) => (
                <tr key={r[0]} className={i % 2 ? "bg-slate-900/20" : ""}>
                  <td className="px-4 py-3 text-slate-200">{r[0]}</td>
                  <td className="px-4 py-3 font-mono text-teal-300">{r[1]}</td>
                  <td className="px-4 py-3 font-mono text-slate-400 hidden sm:table-cell">{r[2]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-slate-500 mt-3">The Dutch statutory minimum wage is around €14/hour (2026). Figures are indicative and vary by employer, region and experience.</p>
      </section>

      <section className="max-w-5xl mx-auto px-5 pb-4">
        <h2 className="font-display font-bold text-2xl mb-6">What you need to know</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Card icon={FileText} title="BSN & paperwork">You'll need a BSN (citizen service number) to work and get paid. EU citizens can start straight away; many employers help you register.</Card>
          <Card icon={Home} title="Housing">Housing is tight in big cities. Many warehouse and production employers offer or arrange accommodation for international staff.</Card>
          <Card icon={Bus} title="Transport">The NL has excellent public transport and cycling infrastructure. Some employers provide transport to distribution centres.</Card>
          <Card icon={Briefcase} title="The 30% ruling">Skilled migrants may qualify for the 30% tax ruling, making up to 30% of salary tax-free for a period — ask your employer.</Card>
          <Card icon={CheckCircle2} title="Health insurance">Basic Dutch health insurance is mandatory once you live and work here. Budget roughly €130–€160/month.</Card>
          <Card icon={Euro} title="Holiday & bonuses">Expect 8% holiday allowance on top of salary, plus shift and overtime bonuses in many blue-collar roles.</Card>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-5 py-14">
        <div className="rounded-3xl overflow-hidden border border-white/10 relative">
          <img src={NL_2} alt="Netherlands countryside" className="w-full h-56 object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 to-slate-950/40 flex items-center">
            <div className="px-8 max-w-lg">
              <h2 className="font-display font-bold text-2xl sm:text-3xl">Ready to work in the Netherlands?</h2>
              <p className="text-slate-300 mt-2 mb-4 text-sm">Create a free profile, upload your CV, and get matched to jobs near you — we'll alert you when new roles appear.</p>
              <div className="flex flex-wrap gap-3">
                <Link to="/register" className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-teal-400 to-cyan-500 text-slate-900 font-semibold">Create profile</Link>
                <Link to="/work/jobs/netherlands" className="px-5 py-2.5 rounded-xl border border-white/15 bg-white/5 text-slate-100 font-medium">Browse NL jobs</Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </WorkLayout>
  );
}
